import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Leaf, Truck, Award, HeartHandshake } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { CategoryCard } from "@/components/CategoryCard";
import { BlogCard } from "@/components/BlogCard";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, blogRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/blog`),
        ]);
        setProducts(productsRes.data.slice(0, 6));
        setCategories(categoriesRes.data);
        setBlogPosts(blogRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thanks for subscribing!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    }
  };

  const benefits = [
    {
      icon: Leaf,
      title: "Rare Plant Selection",
      description: "Unique specimens from around the world for passionate collectors.",
    },
    {
      icon: Award,
      title: "Professional Advice",
      description: "Over 25 years of experience growing and sourcing plants globally.",
    },
    {
      icon: HeartHandshake,
      title: "Quality Assured",
      description: "100% organic products with therapeutic properties.",
    },
    {
      icon: Truck,
      title: "USPS Delivery",
      description: "Reliable shipping to get your products safely to you.",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[85vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1559749619-5f290507d5e6?w=1600"
          alt="Herbal products and plants"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20" />
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="max-w-xl">
              <p className="font-accent text-sm text-bone-cream/90 uppercase tracking-widest mb-4 stagger-1 animate-fade-in opacity-0">
                Welcome to WEH International
              </p>
              <h1 className="font-serif text-4xl md:text-6xl font-bold text-white leading-tight mb-6 stagger-2 animate-fade-in opacity-0">
                Nature's Finest<br />at Your Doorstep
              </h1>
              <p className="font-sans text-lg text-bone-cream/80 mb-8 stagger-3 animate-fade-in opacity-0">
                Discover organic herbal products, rare exotic plants, and homemade goods crafted with care.
              </p>
              <div className="flex flex-wrap gap-4 stagger-4 animate-fade-in opacity-0">
                <Link
                  to="/shop"
                  data-testid="hero-shop-btn"
                  className="bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-8 py-3 font-sans font-medium transition-transform hover:scale-105"
                >
                  Shop Now
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-learn-btn"
                  className="border border-white text-white hover:bg-white hover:text-deep-moss rounded-full px-8 py-3 font-sans font-medium transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
              Why Shop With Us
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900">
              The WEH Difference
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.title}
                  className="text-center p-6"
                  data-testid={`benefit-${index}`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-bone-cream flex items-center justify-center">
                    <Icon className="w-6 h-6 text-deep-moss" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-lg font-medium text-stone-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="font-sans text-sm text-stone-600">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
                Browse By
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900">
                Categories
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-categories-link"
              className="hidden md:flex items-center gap-2 text-deep-moss font-sans text-sm hover:underline"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
                Handpicked For You
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900">
                Featured Products
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-products-link"
              className="hidden md:flex items-center gap-2 text-deep-moss font-sans text-sm hover:underline"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="md:hidden mt-8 text-center">
            <Link
              to="/shop"
              data-testid="view-all-products-mobile"
              className="inline-flex items-center gap-2 text-deep-moss font-sans text-sm hover:underline"
            >
              View All Products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
                From The Blog
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900">
                What's New
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="view-all-blog-link"
              className="hidden md:flex items-center gap-2 text-deep-moss font-sans text-sm hover:underline"
            >
              View All Posts <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 md:py-24 bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <p className="font-accent text-xs text-bone-cream/70 uppercase tracking-widest mb-2">
            Stay Connected
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
            Join Our Mailing List
          </h2>
          <p className="font-sans text-bone-cream/80 mb-8 max-w-md mx-auto">
            Be the first to hear about our latest arrivals, store highlights, and exclusive offers.
          </p>
          <form
            onSubmit={handleNewsletterSubmit}
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="home-newsletter-input"
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-6 py-3 font-sans text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
            />
            <button
              type="submit"
              data-testid="home-newsletter-btn"
              className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-8 py-3 font-sans font-medium transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};
